"use client";

// Third-party Imports
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// Type Imports
import type { entitySettingsTypes } from "@/types/apps/entitySettingsTypes";

// Server Imports
import { doGetEntity } from "@/app/server/actions";

const initialState: entitySettingsTypes = {
  name: "",
  debug: false,
  language: "en",
  logo: undefined,
  logoText: undefined,
  customProfileBanner: undefined,
  profileBanner: undefined,
  userCanSetAddress: true,
  userCanSetLanguage: true,
  userShowsContactDetailsOnProfile: true,
  userShowsTeamsDetailsOnProfile: true,
  userCanSetNotifications: true,
  userCanSeeTeamsPage: true,
  userCanOnlySeeOwnTeams: false,
  userCanSeeTeamDetailsPage: true,
  userCanRequestToJoinTeam: true,
  teamOwnerCanAddUsersToTeam: true,
  teamOwnerCanRequestUserToJoin: true,
  moduleDevelopmentEnabledSprintReviews: false,
  moduleDevelopmentEnabledOwnViewOnly: false,
  enableCareer: true,
  autoFillCareer: false,
  modules: [],
  integrations: [],
  loading: "idle" as string | "pending" as string | "succeeded" as string | "failed" as string,
  error: null,
};

export const doSetSettings = createAsyncThunk<any, any, any>("settings/fetch", async (_: void, { rejectWithValue }) => {
  try {
    const response = await doGetEntity();

    return {
      data: response,
    };
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const entitySettingsSlice = createSlice({
  name: "entitySettings",
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    // Add reducers for additional action types here, and handle loading state as needed
    builder.addCase(doSetSettings.pending, state => {
      state.loading = "idle";
    });
    builder.addCase(doSetSettings.rejected, (state, action) => {
      state.loading = "failed";
      state.error = action.payload;
    });
    builder.addCase(doSetSettings.fulfilled, (state, action) => {
      // Handle null response from server errors
      if (!action.payload?.data) {
        state.loading = "failed";
        state.error = "Failed to load settings";
        
return;
      }

      state.name = action.payload.data.name;
      state.debug = action.payload.data.debug;
      state.language = action.payload.data.language;
      state.logo = action.payload.data.logo;
      state.logoText = action.payload.data.logoText;
      state.customProfileBanner = action.payload.data.customProfileBanner;
      state.profileBanner = action.payload.data.profileBanner;
      state.userCanSetAddress = action.payload.data.userCanSetAddress;
      state.userCanSetLanguage = action.payload.data.userCanSetLanguage;
      state.userShowsContactDetailsOnProfile = action.payload.data.userShowsContactDetailsOnProfile;
      state.userShowsTeamsDetailsOnProfile = action.payload.data.userShowsTeamsDetailsOnProfile;
      state.userCanSetNotifications = action.payload.data.userCanSetNotifications;
      state.userCanSeeTeamsPage = action.payload.data.userCanSeeTeamsPage;
      state.userCanOnlySeeOwnTeams = action.payload.data.userCanOnlySeeOwnTeams;
      state.userCanSeeTeamDetailsPage = action.payload.data.userCanSeeTeamDetailsPage;
      state.userCanRequestToJoinTeam = action.payload.data.userCanRequestToJoinTeam;
      state.teamOwnerCanAddUsersToTeam = action.payload.data.teamOwnerCanAddUsersToTeam;
      state.teamOwnerCanRequestUserToJoin = action.payload.data.teamOwnerCanRequestUserToJoin;
      state.modules = action.payload.data.modules;
      state.enableCareer = action.payload.data.enableCareer;
      state.autoFillCareer = action.payload.data.autoFillCareer;
      state.moduleDevelopmentEnabledSprintReviews = action.payload.data.moduleDevelopmentEnabledSprintReviews;
      state.moduleDevelopmentEnabledOwnViewOnly = action.payload.data.moduleDevelopmentEnabledOwnViewOnly;
      state.integrations = action.payload.data.integrations;
      state.loading = "succeeded";
    });
  },
});

export default entitySettingsSlice.reducer;
